/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  output
} from '@angular/core';
import {
  AlertButtonType,
  AlertIconType,
  errorClassIconList,
  SeverityTypeClassMap
} from '@design-system/components/m-alert-item';
import type {
  AlertItemProps,
  GroupByFieldConfig,
  IAlertPrimaryActionButtonProps,
  IGroupByFieldsConfigs,
  SeverityType
} from '@design-system/components/m-alert-item';
import { colors } from '@design-system/styles/colors';
import { Table, TableStyle, TableModule } from 'primeng/table';
import { TypePillSize } from '../m-type-pill/m-type-pill.component.types';
import { PrimeTemplate } from 'primeng/api';
import { NgClass } from '@angular/common';
import { MTooltipComponent } from '../m-tooltip/m-tooltip.component';
import { MTypePillComponent } from '../m-type-pill/m-type-pill.component';
import { MAvatarComponent } from '../m-avatar/m-avatar.component';
import { MButtonComponent } from '../m-button/m-button.component';
import { MSkeletonComponent } from '../m-skeleton/m-skeleton.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'm-row-group-table',
  templateUrl: './m-row-group-table.component.html',
  styleUrls: [
    '../m-table/m-table.component.scss',
    './m-row-group-table.component.scss'
  ],
  providers: [TableStyle],
  imports: [
    TableModule,
    PrimeTemplate,
    NgClass,
    MTooltipComponent,
    MTypePillComponent,
    MAvatarComponent,
    MButtonComponent,
    MSkeletonComponent
  ]
})
export class MRowGroupTableComponent
  extends Table
  implements OnInit, OnChanges
{
  @Input() sortFields: string[] = [];
  @Input() selectedRowsData: Record<string, any>[] = [];
  @Input() groupByFieldsConfigs: IGroupByFieldsConfigs = {};
  @Input() fieldNametoGroupBy = '';
  @Input() enableRowSelectionCheckbox = true;
  @Input() enableTableHeaderCheckbox = true;
  @Input() tableDataLoading = true;
  @Input() searchInputHasData = false;
  readonly handleAction = output<{
    actionButtonProps: IAlertPrimaryActionButtonProps;
    rowData: AlertItemProps;
  }>();

  readonly selectedRows = output<AlertItemProps[]>();
  readonly requestStackedDataForTable = output<AlertItemProps>();
  readonly alertReadEvent = output<AlertItemProps>();
  readonly resolveActionAlertEvent = output<AlertItemProps>();
  readonly tableState = output<AlertItemProps[]>();
  override scrollHeight = '43.5rem';
  public tagSize = TypePillSize;
  protected readonly colors = colors;
  timeoutValue = 1;
  groupByFieldConfigLength = 0;

  override ngOnInit(): void {
    this.groupByFieldConfigLength = Object.keys(
      this.groupByFieldsConfigs ?? {}
    ).length;
  }

  override ngOnChanges(): void {
    this.computeGroupByFieldsConfigs();
    this.groupByFieldConfigLength = Object.keys(
      this.groupByFieldsConfigs ?? {}
    ).length;
  }

  override toggleRow(rowData: AlertItemProps): void {
    if (rowData.isStacked) {
      this.requestStackedDataForTable.emit(rowData);
    }
    this.computeGroupByFieldsConfigs();

    if (!rowData.isRead && !rowData.expanded) {
      rowData.isRead = true;
      this.alertReadEvent.emit(rowData);
    }
    this.value = this.value.map((row) =>
      row.id === rowData.id ? this.updateRowState(row) : row
    );
  }

  onSelectionChange(selection: AlertItemProps[]) {
    this.selectedRowsData = selection ?? [];
    this.selectedRows.emit(this.selectedRowsData as AlertItemProps[]);
    this.tableState.emit(this.value as AlertItemProps[]);
  }

  private updateRowState(rowData: any): any {
    return {
      ...rowData,
      expanded: !rowData.isStacked ? !rowData.expanded : rowData.expanded
    };
  }

  private computeGroupByFieldsConfigs(): void {
    for (const key in this.groupByFieldsConfigs) {
      if (
        Object.prototype.hasOwnProperty.call(this.groupByFieldsConfigs, key)
      ) {
        const config = this.groupByFieldsConfigs[key] as GroupByFieldConfig;

        // Compute class
        config.computedClass =
          typeof config.class === 'function'
            ? config.class(this.value)
            : config.class;

        // Compute value
        config.computedValue =
          typeof config.value === 'function'
            ? config.value(this.value)
            : config.value;
      }
    }
  }

  getAlertIconClassName(alertIcon: AlertIconType) {
    return errorClassIconList.includes(alertIcon)
      ? 'error-icon'
      : `${alertIcon}-icon`;
  }

  handleActionClick(
    actionButtonProps: IAlertPrimaryActionButtonProps,
    rowData: AlertItemProps
  ): void {
    if (actionButtonProps.type === AlertButtonType.markAsResolved) {
      this.handleMarkAsResolved(rowData);
      return;
    }

    this.handleAction.emit({ actionButtonProps, rowData });
  }

  handleMarkAsResolved(rowData: AlertItemProps) {
    rowData.isVisible = false;
    rowData.isUndoView = true;
    this.timeoutValue = setTimeout(() => {
      rowData.isUndoView = false;
      this.resolveActionAlertEvent.emit(rowData);
      this.cd.markForCheck();
    }, 5000) as unknown as number;
  }

  undoAction(rowData: AlertItemProps) {
    clearInterval(this.timeoutValue);
    rowData.isVisible = true;
    rowData.isUndoView = false;
  }

  getFirstLetterFromEmail(email: string): string {
    return email?.charAt(0).toUpperCase(); // Return the first letter
  }

  getSeverityClassName(severity: SeverityType) {
    return SeverityTypeClassMap[severity];
  }

  // PrimeNG 20 applies the root class on the host via `host: { '[class]': "cn(cx('root'), styleClass)" }`.
  // This wrapper extends Table but renders its OWN inner <p-table>, so that metadata is inherited onto
  // <m-row-group-table> too — duplicating the datatable root styling on the wrapper host.
  // Neutralize on the wrapper host; the inner table is unaffected.
  override cx(): string {
    return '';
  }
}
